import type React from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormControlText,
  FormGroup,
  InputText,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useSWRConfig } from 'swr';
import { toast } from 'react-toastify';
import { generatePath } from '~/utils/generatePath';
import type { Deal } from '~/models/Deal';
import { updateNote } from '~/utils/api/vendors/deals/DealEstimationHeader';
import { isSuccess } from '~/utils/api/api_handler';
import { removeNewlines } from '~/utils/StringUtil';

type Props = {
  deal: Deal;
  onClose: () => void;
  defaultNote: string;
};

export const NoteEditModal: React.FC<Props> = ({
  deal,
  onClose,
  defaultNote
}) => {
  const { mutate: globalMutate } = useSWRConfig();
  const {
    trigger,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isDirty }
  } = useForm<{ note: string }>({
    resolver: yupResolver(object().shape({ note: string().max(400) })),
    defaultValues: { note: defaultNote }
  });
  const noteLength = removeNewlines(watch('note')).length;
  const isInvalid = noteLength > 400;

  const onSubmit = async (): Promise<void> => {
    const result = await updateNote(deal.id, removeNewlines(watch('note')));
    if (isSuccess(result)) {
      toast.success('備考を保存しました。');
      globalMutate(
        generatePath('/vendors/deals/:id/deal_estimation_histories', {
          id: deal.id
        })
      );
      onClose();
    } else {
      toast.error('エラーが発生しました。');
    }
  };

  return (
    <Modalv2 isOpen onClose={onClose}>
      <ModalHeader title="備考の編集" />
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <FormGroup variant="vertical">
            <FormControl isInvalid={isInvalid} label="備考">
              <InputText
                onBlur={(e) => {
                  e.preventDefault();
                  trigger('note');
                  setValue('note', removeNewlines(e.target.value), {
                    shouldDirty: true
                  });
                }}
                onChange={(e) => {
                  setValue('note', e.target.value, { shouldDirty: true });
                }}
                resize="autoRows"
                rows={8}
                value={watch('note')}
              />
              <Flex justifyContent="space-between">
                <FormControlText
                  error={isInvalid ? `${noteLength}/400文字` : undefined}
                  help={[
                    `${noteLength}/400文字`,
                    '見積書の枠内に収めるため、改行は削除されます'
                  ]}
                />
              </Flex>
            </FormControl>
          </FormGroup>
        </ModalBody>
        <ModalFooter
          leftArea={
            <Button
              disabled={isSubmitting}
              onClick={onClose}
              variant="secondary"
            >
              キャンセル
            </Button>
          }
          rightArea={
            <Button
              disabled={!isDirty}
              loading={isSubmitting}
              type="submit"
              variant="primary"
            >
              保存
            </Button>
          }
        />
      </form>
    </Modalv2>
  );
};
